import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UtensilsCrossed, CalendarCheck, DollarSign, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/ai-restaurants.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const AIForRestaurants = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.aiForRestaurants.metaTitle')}</title>
        <meta name="description" content={t('blog.aiForRestaurants.metaDescription')} />
        <meta name="keywords" content="AI for restaurants, restaurant AI phone agent, automated reservation system, restaurant virtual host, AI takeout ordering" />
        <link rel="canonical" href="https://call24x7.ai/blog/ai-for-restaurants" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/ai-for-restaurants" />
        <meta property="og:title" content={t('blog.aiForRestaurants.title')} />
        <meta property="og:description" content={t('blog.aiForRestaurants.metaDescription')} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.aiForRestaurants.title'),
            "description": t('blog.aiForRestaurants.metaDescription'),
            "author": { "@type": "Organization", "name": "Call24x7.AI" },
            "publisher": { "@type": "Organization", "name": "Call24x7.AI" },
            "datePublished": "2025-07-25",
            "dateModified": "2025-07-25"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.aiForRestaurants.title')}
        category={t('blog.categories.restaurants')}
        date="July 25, 2025"
        readTime={`6 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI phone agents for restaurants"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.aiForRestaurants.intro')}
        </p>

        <div className="grid md:grid-cols-4 gap-4 my-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <UtensilsCrossed className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">45%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForRestaurants.stat1')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <CalendarCheck className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">100%</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForRestaurants.stat2')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <DollarSign className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">$800</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForRestaurants.stat3')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Users className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-primary">24/7</div>
              <p className="text-sm text-muted-foreground">{t('blog.aiForRestaurants.stat4')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForRestaurants.dilemmaTitle')}</h2>
        <p>{t('blog.aiForRestaurants.dilemmaDesc1')}</p>
        <p className="mt-4">{t('blog.aiForRestaurants.dilemmaDesc2')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForRestaurants.whatHandlesTitle')}</h2>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.aiForRestaurants.feature1Title')}</h3>
        <p>{t('blog.aiForRestaurants.feature1Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.aiForRestaurants.feature2Title')}</h3>
        <p>{t('blog.aiForRestaurants.feature2Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.aiForRestaurants.feature3Title')}</h3>
        <p>{t('blog.aiForRestaurants.feature3Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('blog.aiForRestaurants.feature4Title')}</h3>
        <p>{t('blog.aiForRestaurants.feature4Desc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForRestaurants.integrationTitle')}</h2>
        <p>{t('blog.aiForRestaurants.integrationIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li><strong>OpenTable:</strong> {t('blog.aiForRestaurants.integration1')}</li>
          <li><strong>Resy:</strong> {t('blog.aiForRestaurants.integration2')}</li>
          <li><strong>Yelp Reservations:</strong> {t('blog.aiForRestaurants.integration3')}</li>
          <li><strong>Toast POS:</strong> {t('blog.aiForRestaurants.integration4')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.aiForRestaurants.resultsTitle')}</h2>
        <Card className="bg-muted/50 my-6">
          <CardContent className="pt-6">
            <p className="italic">{t('blog.aiForRestaurants.testimonial')}</p>
            <p className="mt-4 font-semibold">{t('blog.aiForRestaurants.testimonialAuthor')}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.aiForRestaurants.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.aiForRestaurants.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.aiForRestaurants.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default AIForRestaurants;
